import {call, put, select, takeEvery} from "redux-saga/effects";
import { datadogRum } from "@datadog/browser-rum";
import {
  ADD_VEHICLE,
  SEARCH_BY_LICENSE_PLATE,
  searchByLicensePlate,
  searchByLicensePlateError,
  searchByLicensePlateSuccess,
  selectVehicle,
  SEARCH_BY_VIN,
  searchByVIN,
  searchByVINSuccess,
  searchByVINFailed,
  resetMakeModel,
  updateServiceTypeOptions,
  updateVehicle,
  updateVehicleInfo
} from "../action";
import {
  searchVehicleByLicensePlate,
  searchVehicleByVin
} from "../api/addVehicleApi";
import { cloneDeep } from "lodash";
import { getCommonBenefits,
  getPartnerDetails,
  getRoute,
  getSelectedVehicle,
  getSelectedVehicleColor,
  getSelectedVehicleFuelType,
  getServiceInfo,
} from "../selector";
import { sendVehicleToJacada } from "../jacada/sendJacadaEventData";
import { sendJacadaRequest } from "../api";
import {JJ, FUEL_SERVICE_ID, PLD} from "../app-consts/appConstants";
import { sanitizeLicensePlateForPayload } from "../api/helpers/drwHelpers";

export function* searchVehicleByLicensePlateSaga(
  action: ReturnType<typeof searchByLicensePlate>
) {
  // Determine which route we're on (memberLookup or addVehicle)
  // Make API request based on route
  try {
    const path = yield select(getRoute);
    const plate = sanitizeLicensePlateForPayload(action.payload.licensePlate)
    const response = yield call(searchVehicleByLicensePlate, {
        route: path,
        plate,
        stateCode: action.payload.state,
      });
    if (response?.data?.responseRecords) {
      yield put(searchByLicensePlateSuccess(response.data.responseRecords));
    } else {
      yield put(searchByLicensePlateError(response.data.responseRecords));
    }
  } catch (error) {
    yield put(searchByLicensePlateError(error));
    datadogRum.addError(
      new Error(
        `Vehicle search by License Plate API returned non-2xx status for ${action.payload.licensePlate}. Error: ${error}`
      )
    );
  }
}

export function* searchVehicleByVINSaga(
  action: ReturnType<typeof searchByVIN>
) {
  try {
    const response = yield call(searchVehicleByVin,
      action.payload.VIN,
      action.payload.partnerCode
    );
    yield put(searchByVINSuccess(response.data));
  } catch (error) {
    yield put(searchByVINFailed(error));
    datadogRum.addError(
      new Error(
        `Vehicle search by Vin API returned non-2xx status for ${action.payload.VIN}. Error: ${error}`
      )
    );
  }
}

export function* addVehicleSaga(action: ReturnType<typeof selectVehicle>){
    const { modelYear, makeCode, modelDesc, vin } = yield select(getSelectedVehicle);
    const selectedColor = yield select(getSelectedVehicleColor);
    const color = selectedColor ? selectedColor.charAt(0).toUpperCase() + selectedColor.slice(1).toLowerCase() : "";
    let vehicle: any = {
      color,
      make: makeCode,
      model: modelDesc,
      year: modelYear,
      equipmentClass: "Light",
      vin,
    };

    const partnerDetails = yield select(getPartnerDetails);

    if (partnerDetails.partnerCode === JJ || partnerDetails.partnerCode === PLD) {
      // @ts-ignore
      vehicle.rentalorborrowedflag = "Y";
    }
    const fuelType = yield select(getSelectedVehicleFuelType);
    const serviceInfo = yield select(getServiceInfo);
      if (serviceInfo.id === FUEL_SERVICE_ID && fuelType) {
        yield put(updateServiceTypeOptions({ gas: fuelType }));
      }

    sendVehicleToJacada(vehicle);
    yield put(resetMakeModel());
    const commonBenefits = yield select(getCommonBenefits);
    if (commonBenefits?.benefits) {
      yield put(updateVehicleInfo({ addedVehicleFlag: true }));
      yield put(updateVehicleInfo({ selectedVehicleIndex: commonBenefits.vehicles.length }));
    }
    datadogRum.addAction("Redirect to Add Vehicle page");
    let eventsData = [
      {
        name: "color_name",
        value: color,
      },
      {
        name: "equpmnt_clss_id",
        value: 1,
      }
    ];
    let jacadaRequestData = {
      externalSystemId: localStorage.getItem("conversationId"),
      events: eventsData,
    };
    sendJacadaRequest(jacadaRequestData);
    if (vehicle.rentalorborrowedflag) {
      const sortedVehiclesByHighestCoverage = cloneDeep(
        commonBenefits.vehicles
      );
      sortedVehiclesByHighestCoverage
        .sort((a, b) => a.coverageAmount.localeCompare(b.coverageAmount))
        .reverse();
      vehicle.consumervehicleid =
        sortedVehiclesByHighestCoverage[0]?.vehicleID || "";
    }
    yield put(updateVehicle(vehicle));
}

export function* watchSearchVehicleByLicensePlateVinSaga() {
  yield takeEvery(SEARCH_BY_LICENSE_PLATE, searchVehicleByLicensePlateSaga);
  yield takeEvery(SEARCH_BY_VIN, searchVehicleByVINSaga);
  yield takeEvery(ADD_VEHICLE, addVehicleSaga);
}
function storeAccidentQuestionResponse() {
  throw new Error("Function not implemented.");
}

